import { makeStyles, IconButton } from '@material-ui/core'
import Tooltip from '@material-ui/core/Tooltip'
import { FoldVerticalIcon, UnfoldVerticalIcon } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Tree } from 'react-arborist'
import { AutoSizer } from 'react-virtualized'
import { getLocationsTree } from '../../locations/selectors'
import { Checkbox } from '../../root/components/common'
import { TreeNode } from '../../root/components/common/locations-tree/tree-node'
import {
    treeNodeChildrenAccessor,
    treeNodeIdAccessor,
    useTreeUtils
} from '../../root/components/common/locations-tree/tree-utils'

export const UserLocations = ({
    canAssign,
    locations,
    onAssignedUpdate,
    assignedLocations,
    customerId,
    locationSearchStr
}) => {
    const styles = useStyles()
    const { locale } = useSelector(state => state.main)
    const treeRef = useRef(null)
    const [expanded, setExpanded] = useState(true)

    // Full (unfiltered) location list for the active customer tab. This is what
    // cascade selection and "select all" operate on; search only changes what
    // is displayed, never this.
    const fullLocations = useMemo(
        () => locations?.[customerId]?.locations || [],
        [locations, customerId]
    )

    const assignedSet = useMemo(
        () => new Set(assignedLocations || []),
        [assignedLocations]
    )

    // id -> [all descendant ids], built from parent_id over the FULL list so
    // parent→children cascade and tri-state checkboxes are correct regardless
    // of the current search filter.
    const descendantsMap = useMemo(() => {
        const childrenByParent = {}
        fullLocations.forEach(l => {
            if (l.parent_id) {
                if (!childrenByParent[l.parent_id]) {
                    childrenByParent[l.parent_id] = []
                }
                childrenByParent[l.parent_id].push(l.id)
            }
        })
        const map = {}
        const collect = id => {
            if (map[id]) return map[id]
            const all = []
            ;(childrenByParent[id] || []).forEach(childId => {
                all.push(childId, ...collect(childId))
            })
            map[id] = all
            return all
        }
        fullLocations.forEach(l => collect(l.id))
        return map
    }, [fullLocations])

    // Display tree: when searching, keep matching locations plus their ancestor
    // chain (for hierarchy context) and auto-expand to reveal the matches.
    const treeData = useMemo(() => {
        const query = locationSearchStr.trim().toLowerCase()
        let displayLocations = fullLocations
        if (query) {
            const byId = {}
            fullLocations.forEach(l => (byId[l.id] = l))
            const keep = new Set()
            fullLocations.forEach(l => {
                if (l.name.toLowerCase().includes(query)) {
                    keep.add(l.id)
                    let parentId = l.parent_id
                    while (parentId && byId[parentId] && !keep.has(parentId)) {
                        keep.add(parentId)
                        parentId = byId[parentId].parent_id
                    }
                }
            })
            displayLocations = fullLocations.filter(l => keep.has(l.id))
        }
        const { locationsTreeNoCustomerName } =
            getLocationsTree(displayLocations)
        return locationsTreeNoCustomerName
    }, [fullLocations, locationSearchStr])

    const treeUtils = useTreeUtils(treeData)

    const allIds = useMemo(() => fullLocations.map(l => l.id), [fullLocations])
    const selectedCount = useMemo(
        () => allIds.filter(id => assignedSet.has(id)).length,
        [allIds, assignedSet]
    )
    const allChecked = allIds.length > 0 && selectedCount === allIds.length
    const someChecked = selectedCount > 0 && !allChecked

    // Toggling a node cascades to its whole subtree. Only ids belonging to the
    // current customer are touched, so other customers' assignments are intact.
    const handleSelect = (isSelected, loc) => {
        const ids = [loc.id, ...(descendantsMap[loc.id] || [])]
        const set = new Set(assignedSet)
        ids.forEach(id => (isSelected ? set.add(id) : set.delete(id)))
        onAssignedUpdate([...set])
    }

    const handleSelectAll = () => {
        const set = new Set(assignedSet)
        if (allChecked) {
            allIds.forEach(id => set.delete(id))
        } else {
            allIds.forEach(id => set.add(id))
        }
        onAssignedUpdate([...set])
    }

    const handleToggleAll = () => {
        if (expanded) {
            treeRef.current?.closeAll()
        } else {
            treeRef.current?.openAll()
        }
        setExpanded(!expanded)
    }

    // Re-open the tree after filtered data is rendered.
    useEffect(() => {
        if (locationSearchStr.trim()) {
            treeRef.current?.openAll()
            setExpanded(true)
        }
    }, [locationSearchStr, treeUtils.data])

    return (
        <div className={styles.contentWrapper}>
            <div className={styles.header}>
                {canAssign ? (
                    <Checkbox
                        disableRipple
                        checked={allChecked}
                        indeterminate={someChecked}
                        onClick={handleSelectAll}
                        label={
                            <span className={styles.selectAllLabel}>
                                {allChecked
                                    ? locale.userManagement.deselectAllLocations
                                    : locale.userManagement.selectAllLocations}
                            </span>
                        }
                    />
                ) : (
                    <span />
                )}
                <div className={styles.headerRight}>
                    {canAssign ? (
                        <span className={styles.count}>
                            {selectedCount} / {allIds.length}{' '}
                            {locale.common.assigned}
                        </span>
                    ) : null}
                    <Tooltip
                        title={
                            expanded
                                ? locale.common.collapseTreeNodes
                                : locale.common.expandTreeNodes
                        }>
                        <IconButton
                            className={styles.collapseButton}
                            onClick={handleToggleAll}>
                            {expanded ? (
                                <FoldVerticalIcon size={20} />
                            ) : (
                                <UnfoldVerticalIcon size={20} />
                            )}
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
            <div className={styles.treeWrapper}>
                {treeUtils.data && treeUtils.hasChildren ? (
                    <AutoSizer style={{ paddingBottom: 60 }}>
                        {({ height }) => (
                            <Tree
                                ref={treeRef}
                                indent={24}
                                rowHeight={37}
                                height={height}
                                paddingBottom={40}
                                data={treeUtils.data?.children || []}
                                width={500}
                                rowClassName={styles.treeRow}
                                idAccessor={treeNodeIdAccessor}
                                childrenAccessor={treeNodeChildrenAccessor}
                                openByDefault
                                disableSelect>
                                {props => {
                                    const subtree = [
                                        props.node.data.id,
                                        ...(descendantsMap[
                                            props.node.data.id
                                        ] || [])
                                    ]
                                    const selectedInSubtree = subtree.filter(
                                        id => assignedSet.has(id)
                                    ).length
                                    const checked =
                                        selectedInSubtree === subtree.length
                                    const indeterminate =
                                        selectedInSubtree > 0 && !checked
                                    return (
                                        <TreeNode
                                            isUserMgmt
                                            locationSearchStr={
                                                locationSearchStr
                                            }
                                            isSelected={checked}
                                            indeterminate={indeterminate}
                                            onSelect={
                                                canAssign ? handleSelect : null
                                            }
                                            {...props}
                                        />
                                    )
                                }}
                            </Tree>
                        )}
                    </AutoSizer>
                ) : (
                    <div className={styles.emptyText}>
                        {locale.common.nothingWasFound}
                    </div>
                )}
            </div>
        </div>
    )
}

const useStyles = makeStyles(theme => ({
    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        height: '100%'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: 5,
        marginBottom: 8,
        minHeight: 40
    },
    selectAllLabel: {
        fontWeight: 700,
        fontSize: 13
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center'
    },
    count: {
        fontSize: 12,
        color: theme.textSecondary,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap'
    },
    collapseButton: {
        marginLeft: 5
    },
    treeWrapper: {
        flex: 1,
        width: '100%',
        minHeight: 0
    },
    treeRow: {
        minWidth: '0 !important'
    },
    emptyText: {
        color: theme.textSecondary,
        fontSize: 14,
        paddingTop: 8
    }
}))
